import { json, redirect } from "@remix-run/node";
import { Link, useLoaderData } from "@remix-run/react";
import { motion } from 'framer-motion';
import { getLoggedInUser } from "../services/auth.server";
import { DoctorInLoopModel } from "../models/doctorInLoop.server";
import { User, Stethoscope, ArrowRight, Sparkles, Heart, Shield } from 'lucide-react';
import AnimatedLogo from "../components/AnimatedLogo";

export const loader = async ({ request }) => {
  // Check if user is authenticated
  const user = await getLoggedInUser(request);

  // If authenticated, route to respective dashboards
  if (user) {
    console.log('🔍 [ROOT] User authenticated:', user.email, 'Role:', user.role);
    
    // Check if user is a doctor by looking in doctor_in_loop_doctors table
    try {
      let doctorProfile = await DoctorInLoopModel.getDoctorByUserId(user.id);
      console.log('🔍 [ROOT] Doctor profile by userId:', doctorProfile ? 'FOUND' : 'NOT FOUND');
      
      if (!doctorProfile) {
        // Fallback: check by email
        doctorProfile = await DoctorInLoopModel.getDoctorByEmail(user.email);
        console.log('🔍 [ROOT] Doctor profile by email:', doctorProfile ? 'FOUND' : 'NOT FOUND');
      }
      
      if (doctorProfile) {
        console.log('✅ [ROOT] Doctor profile found, redirecting to doctor dashboard');
        return redirect('/doctor-dashboard');
      }
    } catch (error) {
      console.log('❌ [ROOT] Error checking doctor profile:', user.email, error);
    }
    
    // Check if user is admin
    if (user.role === 'admin') {
      console.log('✅ [ROOT] Admin user, redirecting to admin-dashboard');
      return redirect('/admin-dashboard');
    }
    
    // For patients, redirect to pathology analysis
    try {
      const { PatientModel } = await import('../models/patient.server');
      const patientProfile = await PatientModel.getPatientByEmail(user.email);
      console.log('🔍 [ROOT] Patient profile check:', patientProfile ? 'FOUND' : 'NOT FOUND');
      
      if (patientProfile) {
        console.log('✅ [ROOT] Patient profile found, redirecting to pathology analysis');
        return redirect('/pathology-analysis');
      }
    } catch (error) {
      console.log('❌ [ROOT] Error checking patient profile:', user.email, error);
    }
    
    // If no profiles found, redirect to appropriate onboarding based on user's last login type
    // This is a fallback - ideally users should have completed onboarding
    console.log('⚠️ [ROOT] No profiles found, redirecting to patient onboarding as fallback');
    return redirect('/onboarding');
  }

  // Unauthenticated: render landing page
  return json({ user: null, isAuthenticated: false });
};

export const meta = () => {
  return [
    { title: "HealthQ - Wellness, simplified" },
    { name: "description", content: "Connect with compassionate doctors and get personalized AI insights to nurture your health journey." },
    { name: "viewport", content: "width=device-width, initial-scale=1, maximum-scale=1" },
  ];
};

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

export default function Index() {
  const { user, isAuthenticated } = useLoaderData();
  
  return (
    <div className="min-h-screen bg-[#FDFBF7] text-stone-800 font-sans selection:bg-stone-200 relative overflow-hidden flex flex-col">
      
      {/* Decorative organic shapes */}
      <div className="fixed top-0 left-0 w-[800px] h-[800px] bg-[#E8F0E9] rounded-full blur-3xl -translate-x-1/3 -translate-y-1/3 opacity-70 pointer-events-none" />
      <div className="fixed bottom-0 right-0 w-[600px] h-[600px] bg-[#FFF4E0] rounded-full blur-3xl translate-x-1/3 translate-y-1/3 opacity-70 pointer-events-none" />

      {/* Navigation */}
      <nav className="relative z-10 px-6 py-6 flex justify-between items-center max-w-6xl mx-auto w-full">
        <Link to="/" className="flex items-center gap-2">
          <AnimatedLogo 
            animationType="gentle"
            className="h-10 w-auto"
          />
        </Link>
        <button className="text-stone-500 hover:text-stone-800 font-medium text-sm transition-colors">
          About us
        </button>
      </nav>

      <main className="flex-1 flex flex-col justify-center relative z-10 px-6 py-12">
        <div className="max-w-4xl mx-auto w-full text-center mb-16">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm text-stone-500 text-sm mb-6 border border-stone-100"
          >
            <Sparkles className="w-4 h-4 text-[#D97777]" />
            <span>Clinical grade AI at your disposal</span>
          </motion.div>
          
          <motion.h1 
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="text-5xl md:text-6xl lg:text-7xl font-serif text-stone-900 mb-6 leading-[1.1]"
          >
            Wellness, <span className="italic text-[#5B8C65]">simplified</span>.
          </motion.h1>
          
          <motion.p 
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            transition={{ delay: 0.2 }}
            className="text-xl text-stone-500 max-w-2xl mx-auto font-light leading-relaxed"
          >
            Understand pathology reports outside OPDs. Talk to pathologists on-demand. Neuro-symbolic AI with clinical rigour
          </motion.p>
        </div>

        <div className="max-w-5xl mx-auto w-full grid md:grid-cols-2 gap-6 lg:gap-8">
          
          {/* Patient Card */}
          <Link to="/login">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="group relative bg-white rounded-[2.5rem] p-8 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.02)] border border-stone-100 hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#FFE4E1] rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 opacity-0 group-hover:opacity-50 transition-opacity duration-500" />
              
              <div className="w-14 h-14 bg-[#FFE4E1] rounded-2xl flex items-center justify-center text-[#D97777] mb-6 group-hover:scale-110 transition-transform duration-300">
                <User className="w-7 h-7" />
              </div>
              
              <h2 className="text-3xl font-serif text-stone-800 mb-3">I am a Patient</h2>
              <p className="text-stone-500 mb-8 leading-relaxed">
                Ask questions, upload reports, and receive gentle, data-driven guidance for your wellbeing.
              </p>
              
              <div className="flex items-center gap-2 text-[#D97777] font-medium group-hover:gap-4 transition-all">
                <span>Begin your journey</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </motion.div>
          </Link>

          {/* Doctor Card */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="group relative bg-white rounded-[2.5rem] p-8 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.02)] border border-stone-100 hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#E8F0E9] rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 opacity-0 group-hover:opacity-50 transition-opacity duration-500" />
            
            <div className="w-14 h-14 bg-[#E8F0E9] rounded-2xl flex items-center justify-center text-[#5B8C65] mb-6 group-hover:scale-110 transition-transform duration-300">
              <Stethoscope className="w-7 h-7" />
            </div>
            
            <h2 className="text-3xl font-serif text-stone-800 mb-3">I am a Doctor</h2>
            <p className="text-stone-500 mb-8 leading-relaxed">
              Collaborate with AI to deliver precise, empathetic care and review patient cases efficiently.
            </p>
            
            <div className="flex items-center gap-2 text-[#5B8C65] font-medium group-hover:gap-4 transition-all">
              <span>Access provider portal</span>
              <ArrowRight className="w-4 h-4" />
            </div>
          </motion.div>

        </div>
      </main>

      <footer className="relative z-10 py-8 text-center text-stone-400 text-sm">
        <div className="flex items-center justify-center gap-6 mb-4">
          <span className="flex items-center gap-1.5"><Shield className="w-4 h-4" /> Secure & Private</span>
          <span className="w-1 h-1 rounded-full bg-stone-300" />
          <span>HIPAA Compliant</span>
        </div>
        <p>&copy; 2026 HealthQ. Nurturing better health together.</p>
      </footer>
    </div>
  );
}
